import { useEffect, useState } from "react";
import { useParams } from "react-router";
import {
  ArrowLeft,
  Banknote,
  CheckCircle,
  ChevronDownIcon,
  Clock,
  XCircle,
  InfoIcon,
  Phone,
} from "lucide-react";
import { format } from "date-fns";
import Spinner from "../ui/Spinner/Spinner";
import Alert from "../ui/alert/Alert";
import CurrencyFlag from "./CurrencyFlag";
import ConfirmModal from "./ConfirmModal";
import CopyButton from "../ui/button/copyButton";
import {
  DIAL_CODE_BY_ISO2,
  MOBILE_LENGTH_BY_ISO2,
  SupportedISO2,
} from "../../utils/constant";
import {
  declineWalletLink,
  getWalletLinkConfig,
  getWalletLinkStatus,
  getWalletLinkSummary,
  isNotFound,
  startBankPayment,
  startMomoPayment,
} from "./API/services";
import {
  WalletLinkBankAttempt,
  WalletLinkNetwork,
  WalletLinkPublic,
} from "./types";

type Step =
  | "summary"
  | "pay"
  | "authorize"
  | "bank"
  | "confirming"
  | "done"
  | "alreadyPaid"
  | "declined";

const POLL_MS = 5000;
const AUTHORIZE_SECONDS = 120;

const formatTimer = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

// "6d 04:12:33" / "04:12:33" / "12:33"
const formatLongTimer = (seconds: number) => {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const p = (n: number) => String(n).padStart(2, "0");
  if (d > 0) return `${d}d ${p(h)}:${p(m)}:${p(s)}`;
  if (h > 0) return `${p(h)}:${p(m)}:${p(s)}`;
  return `${p(m)}:${p(s)}`;
};

// Remembers "I have made payment" across a page reload (per link, per tab).
const confirmKey = (token: string) => `wpl-confirming-${token}`;
const readConfirming = (token: string) => {
  try {
    return sessionStorage.getItem(confirmKey(token)) === "1";
  } catch {
    return false;
  }
};
const writeConfirming = (token: string, on: boolean) => {
  try {
    if (on) sessionStorage.setItem(confirmKey(token), "1");
    else sessionStorage.removeItem(confirmKey(token));
  } catch {
    // storage unavailable: the page still works, it just forgets on reload
  }
};

const Logo = () => (
  <div className="mt-1 flex justify-center">
    <img
      className="dark:hidden"
      width={200}
      height={80}
      src="/images/logo/Primary Logo PNG crop1.png"
      alt="Primary Logo for light mode"
    />
    <img
      width={200}
      height={80}
      className="hidden dark:block"
      src="/images/logo/Primary Logo PNG_White new.png"
      alt="Logo for dark mode"
    />
  </div>
);

function WalletPaymentLinkPay() {
  const { token = "" } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [notAvailable, setNotAvailable] = useState(false);
  const [error, setError] = useState("");
  const [link, setLink] = useState<WalletLinkPublic | null>(null);
  const [step, setStep] = useState<Step>("summary");
  const [notice, setNotice] = useState("");

  // Mobile money
  const [networks, setNetworks] = useState<WalletLinkNetwork[] | null>(null);
  const [apiDialCode, setApiDialCode] = useState("");
  const [configLoading, setConfigLoading] = useState(false);
  const [network, setNetwork] = useState<WalletLinkNetwork | null>(null);
  const [networkOpen, setNetworkOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [countdown, setCountdown] = useState(AUTHORIZE_SECONDS);

  // Bank transfer
  const [bank, setBank] = useState<WalletLinkBankAttempt | null>(null);
  const [bankSecondsLeft, setBankSecondsLeft] = useState<number | null>(null);

  // Countdown to the link itself expiring
  const [linkSecondsLeft, setLinkSecondsLeft] = useState<number | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [confirmDecline, setConfirmDecline] = useState(false);
  const [formError, setFormError] = useState("");

  // ---- Load the link
  useEffect(() => {
    if (!token) {
      setNotAvailable(true);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const data = await getWalletLinkSummary(token);
        if (cancelled) return;
        setLink(data);

        if (data.status === "PAID") {
          // Opened again after it was already paid.
          setStep("alreadyPaid");
        } else if (data.status === "DECLINED") {
          setStep("declined");
        } else if (data.status === "PROCESSING") {
          // A payment is already in progress for this link: resume it.
          if (data.rail === "MOMO") {
            setStep("authorize");
          } else if (readConfirming(token)) {
            setStep("confirming");
          } else {
            const attempt = await startBankPayment(token);
            if (cancelled) return;
            setBank(attempt);
            setStep("bank");
          }
        }
      } catch (err: any) {
        if (cancelled) return;
        if (isNotFound(err)) setNotAvailable(true);
        else setError(err?.message || "Something went wrong. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // ---- Load mobile money config (networks + dial code) at the pay step
  useEffect(() => {
    if (step !== "pay" || !link || link.rail !== "MOMO" || networks) return;

    let cancelled = false;
    setConfigLoading(true);

    getWalletLinkConfig(token)
  .then((config: any) => {
    if (cancelled) return;
    setNetworks(
      (config?.networks || []).map((n: any) => ({
        code: n.code,
        name: n.name,
        logo: n.logo,
      }))
    );
    setApiDialCode(config?.dialCode || "");
  })
    // getWalletLinkNetworks(token)
    //   .then((data) => {
    //     if (cancelled) return;
    //     setNetworks(data.networks);
    //     setApiDialCode(data.dialCode);
    //   })
      .catch((err: any) => {
        if (!cancelled)
          setFormError(err?.message || "Could not load mobile networks.");
      })
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [step, link, networks, token]);

  // ---- Poll the link while a payment is in progress
  useEffect(() => {
    if (step !== "authorize" && step !== "bank" && step !== "confirming") return;

    let stopped = false;

    const tick = async () => {
      try {
        const data = await getWalletLinkStatus(token);
        if (stopped) return;
        setLink(data);

        if (data.status === "PAID") {
          writeConfirming(token, false);
          setStep("done");
        } else if (data.status === "ACTIVE") {
          writeConfirming(token, false);
          // The attempt ended without payment (declined, failed or account expired).
          setNotice(
            step === "bank"
              ? "The account details expired before a transfer was received. You can get new details and try again."
              : step === "confirming"
              ? "We have not received your transfer. If you have not paid yet, you can get the account details again."
              : "The payment was not completed. You can try again."
          );
          setStep("pay");
        }
      } catch (err: any) {
        if (!stopped && isNotFound(err)) setNotAvailable(true);
        // Any other error: keep polling.
      }
    };

    const id = window.setInterval(tick, POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [step, token]);

  // ---- Countdown while waiting for the mobile money prompt
  useEffect(() => {
    if (step !== "authorize") return;
    setCountdown(AUTHORIZE_SECONDS);
    const id = window.setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [step]);

  // ---- Countdown to the link expiring
  useEffect(() => {
    if (!link?.expiresAt) {
      setLinkSecondsLeft(null);
      return;
    }
    const expiry = new Date(link.expiresAt).getTime();
    const update = () =>
      setLinkSecondsLeft(Math.max(0, Math.floor((expiry - Date.now()) / 1000)));
    update();
    const id = window.setInterval(update, 1000);
    return () => window.clearInterval(id);
  }, [link?.expiresAt]);

  // ---- Countdown to bank account expiry
  useEffect(() => {
    if (step !== "bank" || !bank?.expiresAt) {
      setBankSecondsLeft(null);
      return;
    }
    const expiry = new Date(bank.expiresAt).getTime();
    const update = () =>
      setBankSecondsLeft(Math.max(0, Math.floor((expiry - Date.now()) / 1000)));
    update();
    const id = window.setInterval(update, 1000);
    return () => window.clearInterval(id);
  }, [step, bank]);

  // ---- Mobile money
  const expectedPhoneLength =
    link?.iso2 && link.iso2 in MOBILE_LENGTH_BY_ISO2
      ? MOBILE_LENGTH_BY_ISO2[link.iso2 as SupportedISO2]
      : 10;

  // The config endpoint is the source of truth; the iso2 map is only a fallback.
  const dialCode =
    apiDialCode ||
    (link?.iso2 && link.iso2 in DIAL_CODE_BY_ISO2
      ? DIAL_CODE_BY_ISO2[link.iso2 as SupportedISO2]
      : "");

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "");
    if (digits.length > expectedPhoneLength) return;
    setPhone(digits);
  };

  const handleMomoSubmit = async () => {
    if (!network) {
      setFormError("Please select a mobile network");
      return;
    }
    if (phone.length !== expectedPhoneLength) {
      setFormError(`Phone number must be ${expectedPhoneLength} digits`);
      return;
    }

    try {
      setSubmitting(true);
      setFormError("");
      setNotice("");
      // Digits only: no "+" and no dial code. The backend adds the dial code.
      await startMomoPayment(token, {
        network: network.code,
        phoneNumber: phone,
      });
      setStep("authorize");
    } catch (err: any) {
      if (isNotFound(err)) setNotAvailable(true);
      else setFormError(err?.message || "Failed to start the payment");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Bank transfer
  const handleBankStart = async () => {
    try {
      setSubmitting(true);
      setFormError("");
      setNotice("");
      const attempt = await startBankPayment(token);
      if (!attempt.accountNumber) {
        setFormError("We could not get the account details. Please try again.");
        return;
      }
      setBank(attempt);
      setStep("bank");
    } catch (err: any) {
      if (isNotFound(err)) setNotAvailable(true);
      else setFormError(err?.message || "Could not get account details");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- "I have made payment": hide the account details and wait for the result
  const handleBankPaid = () => {
    setFormError("");
    setNotice("");
    writeConfirming(token, true);
    setStep("confirming");
  };

  // ---- Decline
  const handleDecline = async () => {
    try {
      setDeclining(true);
      setFormError("");
      await declineWalletLink(token);
      setConfirmDecline(false);
      setLink((prev) => (prev ? { ...prev, status: "DECLINED" } : prev));
      setStep("declined");
    } catch (err: any) {
      if (isNotFound(err)) setNotAvailable(true);
      else {
        setFormError(err?.message || "Could not decline the request");
      }
    } finally {
      setDeclining(false);
    }
  };

  // ---- Render helpers
  const amountText = link
    ? `${link.amount.toLocaleString()} ${link.currency}`
    : "";

  // The link ran out while the payer was still on the summary / pay screens.
  const linkExpired =
    linkSecondsLeft === 0 && (step === "summary" || step === "pay");

  const expiryCountdown =
    linkSecondsLeft !== null && linkSecondsLeft > 0 ? (
      <div className="flex items-center justify-between rounded-lg bg-yellow-50 px-4 py-3 text-sm text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">
        <span>This link expires in</span>
        <span className="font-semibold tabular-nums">
          {formatLongTimer(linkSecondsLeft)}
        </span>
      </div>
    ) : null;

  const backButton = (to: Step) => (
    <button
      onClick={() => {
        setFormError("");
        setStep(to);
      }}
      className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
    >
      <ArrowLeft className="h-5 w-5 mr-1" />
      Back
    </button>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner className="h-10 w-10 text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Something went wrong
        </h2>
        <p className="text-gray-700 dark:text-gray-300">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-grow">
        <Logo />

        <div className="max-w-xl mx-auto p-6 bg-[#F8F7FD] dark:bg-gray-800 shadow-md rounded-lg mt-1 transition-colors duration-300">
          {/* Link not usable */}
          {notAvailable && (
            <div className="text-center space-y-3 py-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                This payment link is not available
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                It may have expired or been cancelled. Please contact the person
                who sent it to you for a new link.
              </p>
            </div>
          )}

          {/* Link ran out while open */}
          {!notAvailable && linkExpired && (
            <div className="text-center space-y-4 py-6">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/30">
                <Clock className="h-7 w-7 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                This payment link has expired
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                {link?.creatorName
                  ? `Please ask ${link.creatorName} for a new link.`
                  : "Please ask the person who sent it for a new link."}
              </p>
            </div>
          )}

          {/* Already paid (link opened again after payment) */}
          {!notAvailable && step === "alreadyPaid" && (
            <div className="text-center space-y-4 py-6">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                <CheckCircle className="h-7 w-7 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                This payment request has already been paid
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                {link?.creatorName
                  ? `The ${amountText} requested by ${link.creatorName} has been paid in full.`
                  : `The ${amountText} requested has been paid in full.`}{" "}
                There is nothing more to do, and you will not be charged again.
                You can close this page.
              </p>
            </div>
          )}

          {/* Waiting for the bank transfer to be confirmed */}
          {!notAvailable && step === "confirming" && link && (
            <div className="text-center space-y-4 py-6">
              <Spinner className="mx-auto h-10 w-10 text-blue-500" />
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Confirming your payment
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                We are checking for your transfer of {amountText}. This page
                updates by itself, so you can keep it open. It can take a few
                minutes for the bank to confirm.
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <Spinner className="h-4 w-4" />
                Waiting for confirmation
              </div>
            </div>
          )}

          {/* Done */}
          {!notAvailable && step === "done" && (
            <div className="text-center space-y-4 py-6">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                <CheckCircle className="h-7 w-7 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Payment successful
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                {amountText} has been paid. You can close this page.
              </p>
            </div>
          )}

          {/* Declined */}
          {!notAvailable && step === "declined" && (
            <div className="text-center space-y-4 py-6">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                <XCircle className="h-7 w-7 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Payment request declined
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                {link?.creatorName
                  ? `You declined the request from ${link.creatorName}.`
                  : "You declined this payment request."}{" "}
                You can close this page.
              </p>
            </div>
          )}

          {/* Step 1: Summary */}
          {!notAvailable && !linkExpired && step === "summary" && link && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                {link.payerName ? `Hi ${link.payerName}, you` : "You"}'ve been
                requested to pay{" "}
                <span className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400">
                  <CurrencyFlag src={link.flag} alt={link.currency} className="h-7 w-7" />
                  {amountText}
                </span>
              </h1>

              {(link.creatorName || link.description) && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
                  {link.creatorName && (
                    <>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        Requested by
                      </h2>
                      <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <p className="text-gray-600 dark:text-gray-300">
                          {link.creatorName}
                        </p>
                      </div>
                    </>
                  )}
                  {link.description && (
                    <>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-4 mb-2">
                        Description
                      </h2>
                      <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <p className="whitespace-pre-wrap break-words text-gray-600 dark:text-gray-300">
                          {link.description}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Payment method
                </h2>
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-gray-600 dark:text-gray-300">
                    {link.rail === "MOMO"
                      ? "Mobile money"
                      : "Bank transfer"}
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Validity Period
                </h2>
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-gray-600 dark:text-gray-300">
                    Valid until{" "}
                    {format(new Date(link.expiresAt), "MMMM do yyyy, h:mm a")}
                  </p>
                </div>
                {expiryCountdown && <div className="mt-3">{expiryCountdown}</div>}
              </div>

              <button
                onClick={() => setStep("pay")}
                className="mt-6 w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-[1.02] active:scale-95"
              >
                Continue to Payment
              </button>

              {formError && (
                <Alert variant="error" title="Error" message={formError} />
              )}

              <button
                onClick={() => setConfirmDecline(true)}
                disabled={declining}
                className="w-full rounded-lg border border-red-300 py-3 px-6 font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                {declining ? (
                  <span className="flex items-center justify-center gap-2">
                    <Spinner className="h-4 w-4" />
                    Declining...
                  </span>
                ) : (
                  "Decline"
                )}
              </button>
            </div>
          )}

          {/* Step 2: Pay */}
          {!notAvailable && !linkExpired && step === "pay" && link && (
            <div className="space-y-6">
              {backButton("summary")}

              {expiryCountdown}

              <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg">
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  You are going to pay:{" "}
                  <span className="text-blue-600 dark:text-blue-400">
                    {amountText}
                  </span>
                </p>
              </div>

              {notice && (
                <div className="flex items-start gap-3 rounded-lg bg-yellow-50 p-4 text-sm text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">
                  <InfoIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <p>{notice}</p>
                </div>
              )}

              {formError && (
                <Alert variant="error" title="Error" message={formError} />
              )}

              {/* Mobile money form */}
              {link.rail === "MOMO" &&
                (configLoading || !networks ? (
                  <div className="flex justify-center py-6">
                    {configLoading && (
                      <Spinner className="h-8 w-8 text-blue-500 dark:text-blue-400" />
                    )}
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Network Provider
                      </label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setNetworkOpen(!networkOpen)}
                          className="w-full px-4 py-3 pr-10 text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-between hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
                        >
                          {network ? (
                            <div className="flex items-center">
                              {network.logo && (
                                <img
                                  src={network.logo}
                                  alt={network.name}
                                  className="w-5 h-5 mr-3"
                                />
                              )}
                              <span>{network.name}</span>
                            </div>
                          ) : (
                            <span className="text-gray-500 dark:text-gray-400">
                              Select a network
                            </span>
                          )}
                          <ChevronDownIcon className="h-5 w-5 text-gray-400 dark:text-gray-300" />
                        </button>

                        {networkOpen && (
                          <div className="absolute z-[100] mt-2 w-full bg-white dark:bg-gray-700 shadow-lg rounded-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-600">
                            <div className="max-h-60 overflow-auto py-1">
                              {networks.length === 0 ? (
                                <p className="px-4 py-2 text-sm text-gray-400">
                                  No networks available
                                </p>
                              ) : (
                                networks.map((n) => (
                                  <div
                                    key={n.code}
                                    className={`flex items-center px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer transition-colors ${
                                      network?.code === n.code
                                        ? "bg-gray-100 dark:bg-gray-600"
                                        : ""
                                    }`}
                                    onClick={() => {
                                      setNetwork(n);
                                      setNetworkOpen(false);
                                    }}
                                  >
                                    {n.logo && (
                                      <img
                                        src={n.logo}
                                        alt={n.name}
                                        className="w-5 h-5 mr-3"
                                      />
                                    )}
                                    <span className="text-gray-900 dark:text-white">
                                      {n.name}
                                    </span>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Phone Number
                      </label>
                      <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <span className="text-gray-500 dark:text-gray-400">
                            +{dialCode}
                          </span>
                        </div>
                        <input
                          type="tel"
                          value={phone}
                          onChange={handlePhoneChange}
                          placeholder="Enter Mobile Money number"
                          maxLength={expectedPhoneLength}
                          className="w-full rounded-lg border border-gray-300 bg-white pl-16 pr-4 py-3 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {`Enter a ${expectedPhoneLength}-digit mobile money number (without +${dialCode}).`}
                      </p>
                    </div>

                    <button
                      onClick={handleMomoSubmit}
                      disabled={submitting || !network || !phone}
                      className={`w-full text-white font-medium py-3 px-6 rounded-lg ${
                        submitting || !network || !phone
                          ? "bg-blue-400 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600"
                      }`}
                    >
                      {submitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <Spinner className="h-4 w-4" />
                          Processing...
                        </span>
                      ) : (
                        "Pay Now"
                      )}
                    </button>
                  </>
                ))}

              {/* Bank transfer start */}
              {link.rail === "BANK" && (
                <>
                  <div className="flex items-start gap-3 rounded-lg bg-green-50 p-4 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
                    <InfoIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <p>
                      You will get the bank account details to pay into.
                      Transfer exactly {amountText} to it.
                    </p>
                  </div>
                  <button
                    onClick={handleBankStart}
                    disabled={submitting}
                    className={`w-full text-white font-medium py-3 px-6 rounded-lg ${
                      submitting
                        ? "bg-blue-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600"
                    }`}
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <Spinner className="h-4 w-4" />
                        Getting account details...
                      </span>
                    ) : (
                      "Get Account Details"
                    )}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Step 3a: Authorize on phone */}
          {!notAvailable && step === "authorize" && link && (
            <div className="text-center space-y-6 py-2">
              <div>
                <div
                  className={`text-4xl font-bold mb-2 ${
                    countdown > 60
                      ? "text-green-500"
                      : countdown > 30
                      ? "text-yellow-500"
                      : "text-red-500"
                  }`}
                >
                  {formatTimer(countdown)}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Time remaining to authorize
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-700">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {amountText}
                </div>
              </div>

              <div>
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                  <Phone className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">
                  Check Your Phone
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Please check your phone for a prompt and enter your mobile
                  money PIN to authorize this payment. Keep this page open, it
                  updates by itself.
                </p>
                {countdown === 0 && (
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    This is taking longer than usual. We are still waiting for
                    confirmation.
                  </p>
                )}
              </div>

              <div className="flex justify-center">
                <Spinner className="h-6 w-6 text-blue-500 dark:text-blue-400" />
              </div>
            </div>
          )}

          {/* Step 3b: Bank transfer details */}
          {!notAvailable && step === "bank" && link && bank && (
            <div className="space-y-6">
              <div className="text-center">
                <Banknote className="mx-auto mb-4 h-12 w-12 text-green-500 dark:text-green-400" />
                <h4 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">
                  Please make a bank transfer to the details below
                </h4>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center dark:border-gray-700 dark:bg-gray-700">
                <div className="flex items-center justify-center gap-2 text-2xl font-bold text-gray-900 dark:text-white">
                  <CurrencyFlag src={link.flag} alt={link.currency} className="h-7 w-7" />
                  {amountText}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Transfer exactly this amount
                </p>
              </div>

              {[
                { label: "Bank Name", value: bank.bankName },
                { label: "Account Number", value: bank.accountNumber },
                { label: "Account Name", value: bank.accountName },
              ].map(
                (row) =>
                  row.value && (
                    <div key={row.label}>
                      <label className="mb-2 block text-xs font-medium text-gray-500 dark:text-gray-400">
                        {row.label}
                      </label>
                      <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-700">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {row.value}
                        </span>
                        <CopyButton text={row.value} />
                      </div>
                    </div>
                  )
              )}

              {bankSecondsLeft !== null && (
                <p className="text-center text-sm text-gray-600 dark:text-gray-300">
                  Please complete your transfer within{" "}
                  <span className="font-semibold">
                    {formatTimer(bankSecondsLeft)}
                  </span>
                  .
                </p>
              )}

              <button
                onClick={handleBankPaid}
                className="w-full rounded-lg bg-blue-600 px-6 py-3 font-medium text-white transition-colors hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600"
              >
                I have made payment
              </button>

              <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <Spinner className="h-4 w-4" />
                Waiting for your transfer. This page updates by itself.
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmDecline}
        title="Decline this request?"
        message="You will not be able to pay it afterwards."
        confirmText="Yes, decline"
        cancelText="Go back"
        loading={declining}
        error={confirmDecline ? formError : ""}
        onConfirm={handleDecline}
        onCancel={() => {
          setFormError("");
          setConfirmDecline(false);
        }}
      />
    </div>
  );
}

export default WalletPaymentLinkPay;